/* oxlint-disable typescript/consistent-type-assertions -- lint to the future batch ignore */
/* oxlint-disable typescript/no-explicit-any -- interfacing with untyped Ember models during migration */
/* oxlint-disable typescript/no-unsafe-assignment -- interfacing with untyped Ember models during migration */
/* oxlint-disable typescript/no-unsafe-member-access -- interfacing with untyped Ember models during migration */
import window from 'ember-window-mock';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Button, type CountryCode } from '@repo/design-system-kit';
import { useNavigate } from '@repo/poly-hooks/routing';
import { useSegment } from '@repo/poly-hooks/segment';
import { useEmberService } from '@qonto/react-migration-toolkit/react/hooks';
import {
  ForeignTaxResidency,
  PepRadioButton,
  PepWithDisclaimer,
  UsTaxResidency,
} from '@repo/customer-registration-kit/common';
import { useContractSignWebSocket } from '@repo/customer-registration-kit/contract-sign';
import { useSentry } from '@repo/shared-kit/sentry';
import { shouldSendToSentry } from '@repo/shared-kit/utils';
import type { AvailableLegalCountryCode } from '@repo/shared-kit/constants';
import { BackButton } from '../back-button';
import { jsURL } from '../../../constants/hosts';
import { postOrganizationRegisterV2 } from '../../register-organization/queries';
import { fetchNavigationOrganizationsClient } from '../../../utils/fetch-navigation-organizations';
import styles from './owner-info-form.module.css';

const FORM_ID = 'owner-info-form';

interface FormData {
  usTaxPayer?: boolean;
  taxPayerOutsideLegalCountry?: boolean;
  usTaxResidency?: { isTaxResident?: boolean; taxNumber?: string };
  foreignTaxResidency?: { isTaxResident?: boolean; country?: CountryCode; taxNumber?: string };
  selfPepDeclaration?: boolean;
  declaredPepInBeneficiaries?: boolean;
}

// TODO: port models to TS
export interface OwnerInfoFormProps {
  registration: any;
  owner: any;
  organization: {
    id: string;
    legalCountry: AvailableLegalCountryCode;
    businessActivityEntityType: string;
    register: () => Promise<void>;
    get: (n: string) => any;
    set: (n: string, v: any) => any;
    save: () => Promise<void>;
  };
  registrationId: string | undefined;
}

export function OwnerInfoForm({
  registration,
  owner,
  organization,
  registrationId,
}: OwnerInfoFormProps): React.ReactElement {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const segment = useSegment();
  const sentry = useSentry();
  const toastFlashMessages = useEmberService('toastFlashMessages') as any;
  const checkout = useEmberService('checkout') as any;
  const userManager = useEmberService('userManager') as any;
  const shouldShowFatcaQuestion = organization.businessActivityEntityType === 'passive_nfe';
  const shouldShowPepInBeneficiaries = registration.isFrFullCompanyCreation;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contractSignWebSocketEnabled, setContractSignWebSocketEnabled] = useState(false);

  function getDefaultValues(): FormData {
    const defaultValues: FormData = {
      foreignTaxResidency: {
        isTaxResident: owner.get('taxPayerOutsideLegalCountry'),
        country: owner.get('otherTaxResidencyCountry'),
        taxNumber: owner.get('otherTaxIdentificationNumber'),
      },
      selfPepDeclaration: owner.get('selfPepDeclaration'),
    };

    if (shouldShowFatcaQuestion) {
      defaultValues.usTaxResidency = {
        isTaxResident: owner.get('usPerson'),
        taxNumber: owner.get('usTaxIdentificationNumber'),
      };
    }

    if (shouldShowPepInBeneficiaries) {
      let declaredPepInBeneficiaries = organization.get('declaredPepInBeneficiaries');

      if (typeof declaredPepInBeneficiaries !== 'boolean') {
        declaredPepInBeneficiaries = false;
        organization.set('declaredPepInBeneficiaries', declaredPepInBeneficiaries);
      }

      defaultValues.declaredPepInBeneficiaries = declaredPepInBeneficiaries;
    }

    return defaultValues;
  }

  const { control, handleSubmit } = useForm<FormData>({
    mode: 'onSubmit',
    defaultValues: getDefaultValues(),
  });

  const updateOwnerInfo = useCallback(
    async (data: FormData): Promise<void> => {
      try {
        /**
         * The `owner` prop comes from the registration model.
         * The registration model can be either `partner-registration`
         * or `registration` Ember data model.
         * In the `registration` model, `owner` is a computed
         * property that returns a direct model instance from
         * the stakeholders array, for which we would use `owner.save()`.
         * In the `partner-registration` model, it is a `PromiseBelongsTo`
         * proxy, for which we need to use `owner.content.save()`
         */
        const ownerModel = owner.content || owner;
        await ownerModel.save();
      } catch (error: unknown) {
        if (shouldSendToSentry(error)) {
          sentry.captureException(error, {
            tags: { component: 'owner-info-form' },
            extra: { registrationId },
          });
        }
      }

      const ownerTaxInfoEventProperties: Record<string, unknown> = {
        is_us_tax_payer: false,
        is_tax_payer_outside_legal_country: false,
      };

      const { usTaxResidency, foreignTaxResidency } = data;

      ownerTaxInfoEventProperties.is_us_tax_payer = usTaxResidency?.isTaxResident;
      ownerTaxInfoEventProperties.is_tax_payer_outside_legal_country =
        foreignTaxResidency?.isTaxResident;

      segment.identify(ownerTaxInfoEventProperties);
      segment.track('onboarding_partner_register_tax_info_submitted', ownerTaxInfoEventProperties);
    },
    [owner, segment, sentry, registrationId]
  );

  const trackAndRedirectAfterContractSign = useCallback(async () => {
    if (registration.isFastTrackWithoutCapitalDeposit) {
      try {
        const organizations = await fetchNavigationOrganizationsClient();
        const foundOrganization = organizations.find(o => o.id === organization.id);

        segment.track('onboarding_partner_register_price_plan_selected', {
          price_plan: 'basic',
        });
        segment.track('onboarding_partner_register_contract_signed');

        if (foundOrganization) {
          window.location.assign(`${jsURL}/organizations/${foundOrganization.slug}`);
        }
      } catch {
        toastFlashMessages.toastError(formatMessage({ id: 'errors.internal-server-error' }));
      }
    } else if (registration.isMicroEnterpriseCreationThoughLegalPlace) {
      try {
        const pricePlan = await checkout.fetchPricePlanSelectedTask.perform(organization.id);

        segment.track('onboarding_partner_register_price_plan_selected', {
          price_plan: pricePlan.selected_products[0].group_code,
        });
        segment.track('onboarding_partner_register_contract_signed');

        navigate(`/registrations/${registration.id}/credentials-success`);
      } catch {
        toastFlashMessages.toastError(formatMessage({ id: 'errors.internal-server-error' }));
      }
    }
  }, [checkout, toastFlashMessages, segment, formatMessage, navigate, registration, organization]);

  const handleContractSignSuccess = useCallback(() => {
    void trackAndRedirectAfterContractSign();
  }, [trackAndRedirectAfterContractSign]);

  const handleContractSignValidationError = useCallback(() => {
    setIsSubmitting(false);
    toastFlashMessages.toastError(formatMessage({ id: 'errors.internal-server-error' }));
  }, [toastFlashMessages, formatMessage]);

  const handleContractSignTimeout = useCallback(() => {
    navigate(`/registrations/${registrationId}/pending-contract`);
  }, [navigate, registrationId]);

  const onSubmit = handleSubmit(async data => {
    setIsSubmitting(true);

    await updateOwnerInfo(data);

    try {
      if (shouldShowPepInBeneficiaries) {
        await organization.save();
      }

      if (
        registration.isFastTrackWithoutCapitalDeposit ||
        registration.isMicroEnterpriseCreationThoughLegalPlace
      ) {
        try {
          await postOrganizationRegisterV2(organization.id);
          setContractSignWebSocketEnabled(true);
        } catch (error) {
          if (shouldSendToSentry(error)) {
            sentry.captureException(error, {
              tags: { component: 'owner-info-form' },
              extra: { registrationId },
            });
          }

          toastFlashMessages.toastError(formatMessage({ id: 'errors.internal-server-error' }));
        }
      } else if (registration.prepaidSource) {
        navigate(`/registrations/${registrationId}/kyc`);
      } else {
        navigate(`/registrations/${registrationId}/price-plans`);
      }
    } catch (error) {
      if (shouldSendToSentry(error)) {
        sentry.captureException(error, {
          tags: { component: 'owner-info-form' },
          extra: { registrationId },
        });
      }
    }
  });

  useContractSignWebSocket({
    token: userManager.websocketToken,
    userId: userManager.currentUser?.id,
    onSuccess: handleContractSignSuccess,
    onValidationError: handleContractSignValidationError,
    onTimeout: handleContractSignTimeout,
    timeoutEnabled: contractSignWebSocketEnabled,
  });

  const handleBackClick = useCallback(() => {
    navigate(`/registrations/${registrationId}/business-finances`);
  }, [navigate, registrationId]);

  return (
    <div>
      <h1 className="heading-section mb-32">
        {formatMessage({ id: 'partner-register.membership.header.additional-information' })}
      </h1>

      <form id={FORM_ID} onSubmit={onSubmit}>
        <div className="mb-32">
          {shouldShowFatcaQuestion ? (
            <Controller
              control={control}
              name="usTaxResidency"
              render={({ field, fieldState: { invalid } }) => (
                <UsTaxResidency
                  isRequired
                  label={formatMessage({
                    id: 'partner-register.membership.description.us-citizen.title',
                  })}
                  onChange={data => {
                    field.onChange(data);
                    owner.set('usPerson', data.isTaxResident);
                    owner.set('usTaxIdentificationNumber', data.taxNumber);
                  }}
                  showErrors={invalid}
                  value={field.value}
                />
              )}
              rules={{
                validate: value => {
                  if (typeof value?.isTaxResident !== 'boolean') {
                    return formatMessage({ id: 'generic.required-field.error' });
                  }
                  if (value.isTaxResident && (!value.taxNumber || value.taxNumber.trim() === '')) {
                    return formatMessage({ id: 'generic.required-field.error' });
                  }
                  return true;
                },
              }}
            />
          ) : null}
        </div>
        <div className="mb-32">
          <Controller
            control={control}
            name="foreignTaxResidency"
            render={({ field, fieldState: { invalid } }) => (
              <ForeignTaxResidency
                blacklistedCountries={[organization.legalCountry]}
                isRequired
                label={formatMessage({
                  id: 'partner-register.membership.description.tax-residency-outside-legal-country.title',
                })}
                onChange={value => {
                  field.onChange(value);
                  owner.set('taxPayerOutsideLegalCountry', value.isTaxResident);
                  owner.set('otherTaxResidencyCountry', value.country);
                  owner.set('otherTaxIdentificationNumber', value.taxNumber);
                }}
                showErrors={invalid}
                value={field.value}
              />
            )}
            rules={{
              validate: value => {
                if (typeof value?.isTaxResident !== 'boolean') {
                  return formatMessage({ id: 'generic.required-field.error' });
                }
                if (value.isTaxResident && !value.country) {
                  return formatMessage({ id: 'generic.required-field.error' });
                }
                if (value.isTaxResident && (!value.taxNumber || value.taxNumber.trim() === '')) {
                  return formatMessage({ id: 'generic.required-field.error' });
                }
                return true;
              },
            }}
          />
        </div>

        <div className="mb-32">
          <Controller
            control={control}
            name="selfPepDeclaration"
            render={({ field, fieldState: { error, invalid } }) => (
              <PepRadioButton
                errorMessage={error?.message}
                isInvalid={invalid}
                onChange={value => {
                  field.onChange(value);
                  owner.set('selfPepDeclaration', value);
                }}
                value={field.value}
              />
            )}
            rules={{
              validate: value => {
                if (typeof value !== 'boolean') {
                  return formatMessage({ id: 'generic.required-field.error' });
                }
                return true;
              },
            }}
          />
        </div>

        {shouldShowPepInBeneficiaries ? (
          <div className="mb-32">
            <Controller
              control={control}
              name="declaredPepInBeneficiaries"
              render={({ field, fieldState: { error, invalid } }) => (
                <PepWithDisclaimer
                  errorMessage={error?.message}
                  isInvalid={invalid}
                  onChange={value => {
                    field.onChange(value);
                    organization.set('declaredPepInBeneficiaries', value);
                  }}
                  subtitle={formatMessage({ id: 'partner-register.owner.pep-ubo.subtitle' })}
                  value={field.value}
                />
              )}
              rules={{
                validate: value => {
                  if (typeof value !== 'boolean') {
                    return formatMessage({ id: 'generic.required-field.error' });
                  }
                  return true;
                },
              }}
            />
          </div>
        ) : null}
      </form>

      <div className={styles.controls}>
        <Button
          data-test-owner-info-continue-button
          form={FORM_ID}
          isPending={isSubmitting}
          stretch
          type="submit"
        >
          {formatMessage({ id: 'generic.continue.cta' })}
        </Button>

        <BackButton className={styles.backButton} onClick={handleBackClick} />
      </div>
    </div>
  );
}
